'use client';

import React, { useRef, useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const destinations = [
  {
    city: 'Kampala',
    country: 'Uganda',
    properties: '1,240+ stays',
    image: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=600&q=80',
    alt: 'Kampala city skyline with modern buildings and lush green hills',
    distance: '0 km away',
    slug: 'kampala',
  },
  {
    city: 'Entebbe',
    country: 'Uganda',
    properties: '380+ stays',
    image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=600&q=80',
    alt: 'Entebbe lakeside with Lake Victoria and botanical gardens',
    distance: '40 km away',
    slug: 'entebbe',
  },
  {
    city: 'Jinja',
    country: 'Uganda',
    properties: '290+ stays',
    image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=600&q=80',
    alt: 'Jinja source of the Nile with adventure activities and river views',
    distance: '80 km away',
    slug: 'jinja',
  },
  {
    city: 'Fort Portal',
    country: 'Uganda',
    properties: '180+ stays',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
    alt: 'Fort Portal with Rwenzori Mountains and crater lakes in the background',
    distance: '300 km away',
    slug: 'fort-portal',
  },
  {
    city: 'Mbarara',
    country: 'Uganda',
    properties: '150+ stays',
    image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&q=80',
    alt: 'Mbarara savanna landscape with rolling hills and traditional homesteads',
    distance: '270 km away',
    slug: 'mbarara',
  },
  {
    city: 'Kabale',
    country: 'Uganda',
    properties: '95+ stays',
    image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=600&q=80',
    alt: 'Kabale highlands with terraced hills and Lake Bunyonyi views',
    distance: '420 km away',
    slug: 'kabale',
  },
  {
    city: 'Nairobi',
    country: 'Kenya',
    properties: '2,100+ stays',
    image: 'https://images.unsplash.com/photo-1611348586804-61bf6c080437?w=600&q=80',
    alt: 'Nairobi modern skyline with Uhuru Park and green spaces',
    distance: 'Kenya',
    slug: 'nairobi',
  },
  {
    city: 'Kigali',
    country: 'Rwanda',
    properties: '890+ stays',
    image: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=600&q=80',
    alt: 'Kigali clean city with rolling hills and modern architecture',
    distance: 'Rwanda',
    slug: 'kigali',
  },
];

export default function PopularDestinations() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-10 px-4 sm:px-6 max-w-7xl mx-auto">
      <div className={`mb-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <h2 className="text-2xl font-bold text-gray-900">Explore nearby destinations</h2>
        <p className="text-gray-500 mt-1">Find your next adventure across East Africa</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {destinations?.map((dest, i) => (
          <Link
            key={dest?.city}
            href={`/destination/${dest?.slug}`}
            className={`group cursor-pointer transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: `${i * 50}ms` }}
          >
            <div className="relative aspect-square rounded-2xl overflow-hidden mb-3">
              <AppImage
                src={dest?.image}
                alt={dest?.alt}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <p className="text-white font-bold text-sm drop-shadow">{dest?.city}</p>
                <p className="text-white/80 text-xs">{dest?.country}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 group-hover:text-[#4088C6] transition-colors">{dest?.city}</p>
              <p className="text-sm text-gray-500">{dest?.distance}</p>
              <p className="text-sm text-gray-500">{dest?.properties}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
